import { useRef, useState } from "react";
import { Camera, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ImageUploadCellProps {
  currentImageUrl?: string | null;
  productName: string;
  disabled?: boolean;
  onUpload: (file: File) => Promise<void>;
}

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 1 * 1024 * 1024; // 1 MB
const MAX_DIM   = 1200;

function compressToWebP(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        if (width >= height) {
          height = Math.round((height * MAX_DIM) / width);
          width  = MAX_DIM;
        } else {
          width  = Math.round((width * MAX_DIM) / height);
          height = MAX_DIM;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width  = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) { reject(new Error("Canvas context unavailable")); return; }

      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error("Compression failed")); return; }
          resolve(new File([blob], "photo.webp", { type: "image/webp" }));
        },
        "image/webp",
        0.8
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

export const ImageUploadCell = ({
  currentImageUrl,
  productName,
  disabled = false,
  onUpload,
}: ImageUploadCellProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [localUrl, setLocalUrl] = useState<string | null>(null);

  const displayUrl = localUrl ?? currentImageUrl ?? null;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files?.[0];
    if (!raw) return;

    // Reset input so same file can be re-selected if needed
    e.target.value = "";

    // Format validation
    if (!ACCEPTED_TYPES.includes(raw.type)) {
      toast.error("Formato inválido. Use JPG, PNG ou WebP.");
      return;
    }

    // Size validation (before compression)
    if (raw.size > MAX_BYTES * 5) {
      toast.error("Arquivo muito grande. Máximo 5 MB antes da compressão.");
      return;
    }

    try {
      setIsUploading(true);

      // Compress + convert to WebP
      const compressed = await compressToWebP(raw);

      if (compressed.size > MAX_BYTES) {
        toast.error("Arquivo ainda maior que 1 MB após compressão. Use uma imagem menor.");
        return;
      }

      // Optimistic local preview
      const previewUrl = URL.createObjectURL(compressed);
      setLocalUrl(previewUrl);

      await onUpload(compressed);

      // Keep preview until parent updates prop
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Erro ao enviar foto. Tente novamente.");
      setLocalUrl(null);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="relative group w-12 h-12">
      {/* Thumbnail */}
      <div
        className={cn(
          "w-12 h-12 rounded border border-border overflow-hidden flex items-center justify-center bg-muted/40",
          !disabled && "cursor-pointer"
        )}
        onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
        title={productName}
      >
        {displayUrl ? (
          <img
            src={displayUrl}
            alt={productName}
            className="w-full h-full object-cover"
          />
        ) : (
          <Camera className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      {/* Upload overlay on hover */}
      {!disabled && !isUploading && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="absolute inset-0 bg-black/40 rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
          title="Alterar foto"
        >
          <Upload className="h-4 w-4 text-white" />
        </button>
      )}

      {/* Uploading spinner */}
      {isUploading && (
        <div className="absolute inset-0 bg-background/70 rounded flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
        capture="environment"
        disabled={disabled || isUploading}
      />
    </div>
  );
};
